/**
 * Storage adapter interfaces and implementations
 */

import type { CloudStorageApi } from './cloudStorageApi';

/**
 * Generic storage adapter interface
 */
export interface StorageAdapter {
  // Basic operations
  getItem(key: string): Promise<string | undefined>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  getKeys(): Promise<string[]>;
  clear(): Promise<void>;

  // Batch operations (optional)
  getItems?(keys: string[]): Promise<Record<string, string>>;
  setItems?(items: Record<string, string>): Promise<void>;
  removeItems?(keys: string[]): Promise<void>;

  // Metadata
  isAvailable(): boolean;
  readonly type: 'cloudStorage' | 'localStorage';
}

/**
 * LocalStorage adapter with Promise API
 */
export class LocalStorageAdapter implements StorageAdapter {
  readonly type = 'localStorage' as const;

  isAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  async getItem(key: string): Promise<string | undefined> {
    try {
      const value = localStorage.getItem(key);
      return value ?? undefined;
    } catch {
      return undefined;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      throw new Error(`Failed to set item in localStorage: ${error}`);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      throw new Error(`Failed to remove item from localStorage: ${error}`);
    }
  }

  async getKeys(): Promise<string[]> {
    try {
      return Object.keys(localStorage);
    } catch {
      return [];
    }
  }

  async clear(): Promise<void> {
    try {
      localStorage.clear();
    } catch (error) {
      throw new Error(`Failed to clear localStorage: ${error}`);
    }
  }

  async getItems(keys: string[]): Promise<Record<string, string>> {
    const result: Record<string, string> = {};
    for (const key of keys) {
      const value = await this.getItem(key);
      if (value !== undefined) {
        result[key] = value;
      }
    }
    return result;
  }

  async removeItems(keys: string[]): Promise<void> {
    for (const key of keys) {
      await this.removeItem(key);
    }
  }
}

/**
 * CloudStorage adapter with automatic retry and error handling
 */
export class CloudStorageAdapter implements StorageAdapter {
  readonly type = 'cloudStorage' as const;

  constructor(private cloudStorageApi: CloudStorageApi) {}

  isAvailable(): boolean {
    return this.cloudStorageApi.isAvailable();
  }

  async getItem(key: string): Promise<string | undefined> {
    return this.cloudStorageApi.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    return this.cloudStorageApi.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    return this.cloudStorageApi.removeItem(key);
  }

  async getKeys(): Promise<string[]> {
    return this.cloudStorageApi.getKeys();
  }

  async clear(): Promise<void> {
    const keys = await this.getKeys();
    await this.cloudStorageApi.removeItems(keys);
  }

  async getItems(keys: string[]): Promise<Record<string, string>> {
    return this.cloudStorageApi.getItems(keys);
  }

  async setItems(items: Record<string, string>): Promise<void> {
    const promises = Object.entries(items).map(
      ([key, value]) => this.setItem(key, value),
    );
    await Promise.all(promises);
  }

  async removeItems(keys: string[]): Promise<void> {
    return this.cloudStorageApi.removeItems(keys);
  }
}

/**
 * Hybrid adapter that tries CloudStorage first, falls back to localStorage
 */
export class HybridStorageAdapter implements StorageAdapter {
  readonly type = 'cloudStorage' as const;

  private currentAdapter: StorageAdapter;
  private cloudAdapter: CloudStorageAdapter;
  private localAdapter: LocalStorageAdapter;
  private hasCloudStorageFailed = false;

  constructor(
    cloudStorageApi: CloudStorageApi,
  ) {
    this.cloudAdapter = new CloudStorageAdapter(cloudStorageApi);
    this.localAdapter = new LocalStorageAdapter();

    // Start with CloudStorage if available
    this.currentAdapter = this.cloudAdapter.isAvailable()
      ? this.cloudAdapter
      : this.localAdapter;
  }

  isAvailable(): boolean {
    return this.currentAdapter.isAvailable();
  }

  private async executeWithFallback<T>(
    operation: (adapter: StorageAdapter) => Promise<T>,
  ): Promise<T> {
    try {
      return await operation(this.currentAdapter);
    } catch (error) {
      // If CloudStorage fails and we haven't fallen back yet
      if (this.currentAdapter === this.cloudAdapter && !this.hasCloudStorageFailed) {
        // eslint-disable-next-line no-console
        console.warn('CloudStorage operation failed, falling back to localStorage', error);
        this.hasCloudStorageFailed = true;
        this.currentAdapter = this.localAdapter;

        // Retry with localStorage
        return operation(this.localAdapter);
      }
      throw error;
    }
  }

  async getItem(key: string): Promise<string | undefined> {
    return this.executeWithFallback((adapter) => adapter.getItem(key));
  }

  async setItem(key: string, value: string): Promise<void> {
    return this.executeWithFallback((adapter) => adapter.setItem(key, value));
  }

  async removeItem(key: string): Promise<void> {
    return this.executeWithFallback((adapter) => adapter.removeItem(key));
  }

  async getKeys(): Promise<string[]> {
    return this.executeWithFallback((adapter) => adapter.getKeys());
  }

  async clear(): Promise<void> {
    return this.executeWithFallback((adapter) => adapter.clear());
  }

  async getItems(keys: string[]): Promise<Record<string, string>> {
    return this.executeWithFallback((adapter) => {
      if (adapter.getItems) {
        return adapter.getItems(keys);
      }
      // Fallback implementation
      return Promise.all(keys.map((key) => adapter.getItem(key))).then((values) => {
        const result: Record<string, string> = {};
        keys.forEach((key, i) => {
          if (values[i] !== undefined) {
            result[key] = values[i]!;
          }
        });
        return result;
      });
    });
  }

  async removeItems(keys: string[]): Promise<void> {
    return this.executeWithFallback((adapter) => {
      if (adapter.removeItems) {
        return adapter.removeItems(keys);
      }
      // Fallback implementation
      return Promise.all(keys.map((key) => adapter.removeItem(key))).then(() => {});
    });
  }

  getCurrentAdapterType(): 'cloudStorage' | 'localStorage' {
    return this.currentAdapter.type;
  }
}
